/**
 * Destaque vindo de listas (§9.11.4; tipo em `model/types.ts`, DE-08 = DE-B04) e a ponte entre a parte estática de
 * `features/map` (chunk `index`: `MapTopBar`, `MapLegend`, atalhos do App) e o chunk do mapa (`ElectionMap`, por
 * `import()`; DE-06). Este arquivo fica no `index`: nada aqui importa VALOR de `src/map/` (arrastaria o chunk do mapa
 * para o crítico, DE-C20); de lá, só tipos.
 *
 * A ponte tem dois lados, cada um com escritor único:
 * - `mapLegendData` (escrito só pelo `ElectionMap`): contagens e limiares saídos do MESMO `MapPaint` do desenho
 *   (§9.6, AC-9.6-07). `null` enquanto a geometria não chegou (a legenda espera o 1º desenho, §9.6 estado
 *   "Carregando geometria") ou com o mapa desmontado.
 * - `registerMapZoom` / `mapZoom`: o `ElectionMap` registra o zoom; o App injeta `mapZoom` nos atalhos `+`, `-` e `0`
 *   (§5.7, `ShortcutHandlers.zoom`). Sem mapa montado, `mapZoom` devolve false (não agiu).
 */
import { signal } from '@preact/signals';
import type { MapLevel, MapLink, MapMode, PlaceCode, RouteOffice } from '../../model/types';

export type { MapLink };

// ── Destaque vindo de listas (§9.11.4) ───────────────────────────────────────────────────────────────────────────
/** Destaque de um lugar (UF ou ZZ). Quem escreve: `UpdateItem`, `UfStrip`, `RaceRow` (Fase 6 e 9). */
export function placeLink(code: PlaceCode): MapLink {
  return { kind: 'place', code };
}

/** Mesmo destaque (lugar pelo código; zona por município e número). */
export function sameMapLink(a: MapLink | null, b: MapLink | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind === 'place' && b.kind === 'place') return a.code === b.code;
  if (a.kind === 'zone' && b.kind === 'zone') return a.ibge === b.ibge && a.zone === b.zone;
  return false;
}

/** Lugar destacado (UF ou ZZ), ou null (zona ou nenhum). */
export function linkedPlace(link: MapLink | null): PlaceCode | null {
  return link?.kind === 'place' ? link.code : null;
}

// ── Legenda (§9.6) ───────────────────────────────────────────────────────────────────────────────────────────────
/** Unidade da legenda pelo nível desenhado (§9.6): o substantivo concorda com o último número. */
export type LegendUnit = 'municipalities' | 'states' | 'cities' | 'zones';

export interface LegendCandidate {
  /** Lado do candidato escolhido no par [A, B] (§9.4.7). */
  side: 0 | 1;
  /** Limiares s0…s4 (frações) de §9.5.1, pela fatia nacional S. */
  steps: number[];
  /** Lugares em que o candidato lidera (na unidade da legenda). */
  ahead: number;
  /** Lugar de maior fatia dele entre os com dado (título "Mais forte em…"), ou null. */
  strongest: { code: PlaceCode; share: number } | null;
}

/** O que a legenda lê do desenho (escrito pelo `ElectionMap` a cada `MapPaint`). */
export interface MapLegendData {
  office: RouteOffice;
  /** Modo EFETIVO (§9.4.1). */
  mode: MapMode;
  level: MapLevel;
  unit: LegendUnit;
  /** Lugares liderados por [A, B] com o mesmo `MapPaint` do desenho; empates e "sem dados" não contam. */
  lead: [number, number];
  /** Lugares com 100% apurado (degrau 4 de Apurado). */
  complete: number;
  /** Total de lugares da unidade (27 estados; municípios e cidades do cadastro, nunca constante, P-06). */
  total: number;
  /** Lugares com alguma cor de dado. */
  withData: number;
  /** Só no modo Candidato. */
  candidate: LegendCandidate | null;
  /** Governadores: partido (`party_code`) × UFs lideradas, ordem de §9.6 (mais UFs; empate pelo número). */
  parties: Array<{ party: string; n: number }>;
}

/** (escritor: `ElectionMap`) */
export const mapLegendData = signal<MapLegendData | null>(null);

// ── Zoom pelos atalhos (§5.7, §9.8.4) ────────────────────────────────────────────────────────────────────────────
export type MapZoomAction = 'in' | 'out' | 'reset';
type ZoomHandler = (action: MapZoomAction) => boolean;

let zoomHandler: ZoomHandler | null = null;

/** O `ElectionMap` registra o zoom ao montar; devolve o cancelamento (só cancela se ainda for o registrado). */
export function registerMapZoom(handler: ZoomHandler): () => void {
  zoomHandler = handler;
  return () => {
    if (zoomHandler === handler) zoomHandler = null;
  };
}

/** Atalho de zoom (`ShortcutHandlers.zoom`): true se o mapa agiu; false sem mapa montado, na TV ou no limite. */
export function mapZoom(action: MapZoomAction): boolean {
  return zoomHandler ? zoomHandler(action) : false;
}
